import i18next from 'i18next';
import moment from 'moment';
import 'moment/locale/fr';
import 'moment/locale/fr-ca';

type RelativeTimeShortConfig = {
  s: string;
  ss: string;
  m: string;
  mm: string;
  h: string;
  hh: string;
  d: string;
  dd: string;
};

const RELATIVE_TIME_SHORT_KEYS: (keyof RelativeTimeShortConfig)[] = [
  's',
  'ss',
  'm',
  'mm',
  'h',
  'hh',
  'd',
  'dd',
];

const DEFAULT_RELATIVE_TIME_SHORT_EN: RelativeTimeShortConfig = {
  s: 'secs',
  ss: '%d secs',
  m: '1 min',
  mm: '%d mins',
  h: '1 hr',
  hh: '%d hrs',
  d: '1 day',
  dd: '%d days',
};

const DEFAULT_RELATIVE_TIME_SHORT_FR: RelativeTimeShortConfig = {
  s: 'qq secs',
  ss: '%d secs',
  m: '1 min',
  mm: '%d min',
  h: '1 h',
  hh: '%d h',
  d: '1 j',
  dd: '%d j',
};

function readRelativeTimeShortFromTranslations(
  lng: 'en' | 'fr',
): RelativeTimeShortConfig {
  const fallback =
    lng === 'fr'
      ? DEFAULT_RELATIVE_TIME_SHORT_FR
      : DEFAULT_RELATIVE_TIME_SHORT_EN;

  try {
    const raw = i18next.t('moment.relativeTimeShort', {
      lng,
      returnObjects: true,
    }) as unknown;

    if (!raw || typeof raw !== 'object') return fallback;

    const obj = raw as Record<string, unknown>;
    const next: Partial<RelativeTimeShortConfig> = {};

    for (const key of RELATIVE_TIME_SHORT_KEYS) {
      const value = obj[key];
      if (typeof value !== 'string' || value.length === 0) {
        return fallback;
      }
      next[key] = value;
    }

    return next as RelativeTimeShortConfig;
  } catch {
    return fallback;
  }
}

let configured = false;

/**
 * Configures Moment.js locale settings for shortened relative time strings.
 * Should be called once during app initialization or when locale changes.
 *
 * This is a global side effect that updates Moment's locale configuration
 * to use abbreviated units (e.g., "5 mins ago" instead of "5 minutes ago").
 */
export function configureMomentRelativeTime() {
  if (configured) return;
  configured = true;

  const en = readRelativeTimeShortFromTranslations('en');
  moment.updateLocale('en', { relativeTime: en });
  moment.updateLocale('en-ca', { relativeTime: en });

  const fr = readRelativeTimeShortFromTranslations('fr');
  moment.updateLocale('fr', { relativeTime: fr });
  moment.updateLocale('fr-ca', { relativeTime: fr });
}

/**
 * Forces reconfiguration of Moment locale settings.
 * Useful when i18n translations are loaded asynchronously.
 */
export function reconfigureMomentRelativeTime() {
  configured = false;
  configureMomentRelativeTime();
}
